import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  HostListener,
  inject,
  Input,
  OnChanges,
  QueryList,
  signal,
  Signal,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import {TableAction} from './table-action.model';
import {SvgIconComponent} from '../svg-icon/svg-icon.component';
import {DtActionsService} from '../data-table/services/dt-actions.service';
import {ResolveLabelPipe} from '../../pipes/resolve-label/resolve-label-pipe';

@Component({
  selector: 'app-table-actions',
  standalone: true,
  imports: [SvgIconComponent, ResolveLabelPipe],
  templateUrl: './table-actions.component.html',
  styleUrls: ['./table-actions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableActionsComponent implements OnChanges, AfterViewInit {
  /**
   * Optional manual actions. If provided, they override the dynamic actions from DtActionsService.
   * Note: These won't be enhanced by the registry unless you handle them yourself.
   */
  @Input() actions: TableAction[] = [];

  /**
   * Optional tableId to target a specific table.
   * If not provided, it will use the active table from the registry.
   */
  @Input() tableId?: string;

  private readonly actionsService = inject(DtActionsService);

  /** Breakpoint mobile, allineato a `$mobile-breakpoint` in table-actions.component.scss. */
  private static readonly MOBILE_BREAKPOINT = 768;

  /** True quando la viewport è in fascia mobile (≤ breakpoint). */
  readonly isMobileViewport = signal<boolean>(
    typeof window !== 'undefined' && window.innerWidth <= TableActionsComponent.MOBILE_BREAKPOINT,
  );

  /**
   * Max visible buttons before collapsing to "ALTRO" (configurabile via
   * DtActionsService). Su viewport mobile, se la pagina ha impostato un limite
   * dedicato (`mobileMaxVisible`), quello ha precedenza; altrimenti vale
   * `maxVisible` (comportamento desktop invariato).
   */
  readonly MAX_VISIBLE = computed(() => {
    const mobileMax = this.actionsService.mobileMaxVisible();
    if (this.isMobileViewport() && mobileMax != null) {
      return mobileMax;
    }
    return this.actionsService.maxVisible();
  });

  /**
   * Reactive actions from the service, enhanced by the table state.
   */
  private readonly dynamicActions = this.actionsService.getEnhancedActions(this.tableId);

  /**
   * Final actions to display: manual @Input() or dynamic from service.
   */
  readonly effectiveActions: Signal<TableAction[]> = computed(() => {
    return this.actions.length > 0 ? this.actions : this.dynamicActions();
  });

  // ── ARIA IDs ───────────────────────────────────────────────────────────────
  readonly dropdownMenuId = `tac-menu-${Math.random().toString(36).slice(2, 8)}`;
  readonly mobileDropdownMenuId = `tac-menu-mob-${Math.random().toString(36).slice(2, 8)}`;

  // ── Computed action arrays ─────────────────────────────────────────────────
  readonly mainActions = computed(() => this.effectiveActions().slice(0, this.MAX_VISIBLE()));
  readonly dropdownActions = computed(() => this.effectiveActions().slice(this.MAX_VISIBLE()));
  /** Tutte le azioni: usato dal dropdown mobile "Azioni" */
  readonly allActions = computed(() => this.effectiveActions());

  /** True quando il layout mobile deve mostrare pulsanti inline invece del dropdown */
  readonly isMobileButtons = computed(() => this.actionsService.mobileMode() === 'buttons');

  // ── Dropdown state ─────────────────────────────────────────────────────────
  isDropdownOpen = false; // desktop "ALTRO"
  isMobileDropdownOpen = false; // mobile "Azioni"

  private focusedDropdownIndex = -1;
  private focusedMobileDropdownIndex = -1;

  // ── View references ────────────────────────────────────────────────────────
  @ViewChildren('dropdownItem')
  dropdownItems!: QueryList<ElementRef<HTMLButtonElement>>;

  @ViewChildren('mobileDropdownItem')
  mobileDropdownItems!: QueryList<ElementRef<HTMLButtonElement>>;

  private readonly elRef = inject(ElementRef);
  private readonly cdr = inject(ChangeDetectorRef);

  ngOnChanges(changes: SimpleChanges): void {
    // Logic moved to computed signals
  }

  ngAfterViewInit(): void {}

  // ─── Color Logic ──────────────────────────────────────────────────────────

  getButtonClass(index: number, action: TableAction): string {
    const variant = action.variant ?? (index === 0 ? 'success' : 'primary');
    return `btn btn--${variant}`;
  }

  // ─── Desktop Dropdown ─────────────────────────────────────────────────────

  toggleDropdown(): void {
    this.isDropdownOpen = !this.isDropdownOpen;
    if (this.isDropdownOpen) {
      this.focusedDropdownIndex = -1;
      setTimeout(() => this.moveFocus(this.dropdownItems, this.focusedDropdownIndex, 1), 50);
    }
  }

  closeDropdown(returnFocusToTrigger = false): void {
    this.isDropdownOpen = false;
    this.focusedDropdownIndex = -1;
    this.cdr.markForCheck();
    if (returnFocusToTrigger) {
      const trigger = this.elRef.nativeElement.querySelector('.tac-desktop .btn--more') as HTMLButtonElement | null;
      trigger?.focus();
    }
  }

  onTriggerKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggleDropdown();
    }
  }

  onDropdownKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.focusedDropdownIndex = this.moveFocus(this.dropdownItems, this.focusedDropdownIndex, 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.focusedDropdownIndex = this.moveFocus(this.dropdownItems, this.focusedDropdownIndex, -1);
        break;
      case 'Escape':
        event.preventDefault();
        this.closeDropdown(true);
        break;
      case 'Tab':
        this.closeDropdown(false);
        break;
    }
  }

  // ─── Mobile Dropdown ──────────────────────────────────────────────────────

  toggleMobileDropdown(): void {
    this.isMobileDropdownOpen = !this.isMobileDropdownOpen;
    if (this.isMobileDropdownOpen) {
      this.focusedMobileDropdownIndex = -1;
      setTimeout(() => this.moveFocus(this.mobileDropdownItems, this.focusedMobileDropdownIndex, 1), 50);
    }
  }

  closeMobileDropdown(returnFocusToTrigger = false): void {
    this.isMobileDropdownOpen = false;
    this.focusedMobileDropdownIndex = -1;
    this.cdr.markForCheck();
    if (returnFocusToTrigger) {
      const trigger = this.elRef.nativeElement.querySelector('.tac-desktop .btn--more') as HTMLButtonElement | null;
      trigger?.focus();
    }
  }

  onMobileTriggerKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggleMobileDropdown();
    }
  }

  onMobileDropdownKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.focusedMobileDropdownIndex = this.moveFocus(this.mobileDropdownItems, this.focusedMobileDropdownIndex, 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.focusedMobileDropdownIndex = this.moveFocus(this.mobileDropdownItems, this.focusedMobileDropdownIndex, -1);
        break;
      case 'Escape':
        event.preventDefault();
        this.closeMobileDropdown(true);
        break;
      case 'Tab':
        this.closeMobileDropdown(false);
        break;
    }
  }

  // ─── Shared Focus Logic ───────────────────────────────────────────────────

  /**
   * Sposta il focus tra gli elementi di un dropdown.
   * Salta i button disabilitati e wrappa a capo.
   * Restituisce il nuovo indice corrente.
   */
  private moveFocus(
    items: QueryList<ElementRef<HTMLButtonElement>>,
    currentIndex: number,
    direction: 1 | -1
  ): number {
    const arr = items?.toArray() ?? [];
    if (!arr.length) return currentIndex;

    let next = currentIndex + direction;
    if (next < 0) next = arr.length - 1;
    if (next >= arr.length) next = 0;

    let attempts = 0;
    while (arr[next]?.nativeElement.disabled && attempts < arr.length) {
      next += direction;
      if (next < 0) next = arr.length - 1;
      if (next >= arr.length) next = 0;
      attempts++;
    }

    if (!arr[next]?.nativeElement.disabled) {
      arr[next].nativeElement.focus();
      return next;
    }

    return currentIndex;
  }

  // ─── Close on outside click ───────────────────────────────────────────────

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elRef.nativeElement.contains(event.target)) {
      if (this.isDropdownOpen) this.closeDropdown(false);
      if (this.isMobileDropdownOpen) this.closeMobileDropdown(false);
    }
  }

  /** Aggiorna la fascia di viewport per lo split mobile/desktop dei pulsanti. */
  @HostListener('window:resize')
  onWindowResize(): void {
    this.isMobileViewport.set(
      typeof window !== 'undefined' && window.innerWidth <= TableActionsComponent.MOBILE_BREAKPOINT,
    );
  }

  // ─── Action Execution ─────────────────────────────────────────────────────

  /**
   * @param tableAction   Azione da eseguire
   * @param closeDesktop  Chiude il dropdown desktop dopo l'esecuzione
   * @param closeMobile   Chiude il dropdown mobile dopo l'esecuzione
   */
  executeAction(
    tableAction: TableAction,
    closeDesktop = false,
    closeMobile = false
  ): void {
    const isDisabled = typeof tableAction.isDisabled === 'function'
      ? tableAction.isDisabled()
      : tableAction.isDisabled;

    if (isDisabled) return;
    if (closeDesktop) this.closeDropdown(false);
    if (closeMobile) this.closeMobileDropdown(false);
    tableAction.action();
  }
}
